import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import type { TestResultDashboardDateField } from '@risksmart-app/domain/src/types/consts/dashboard-date-fields';
import type { TestResultsRegisterResponseRow } from '@risksmart-app/drizzle/src/dto';
import type {
  GetWidgetTestResultsQuery,
  Test_Result_Bool_Exp,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  GetWidgetTestResultsDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import useEntityWhereFilter from 'src/hooks/useEntityWhereFilter';
import { createQueryHook } from 'src/utils';

import { TEST_RESULT_DATE_PREDICATES } from '@/hooks/queries/dashboardDatePredicates';
import {
  buildDashboardWhere,
  type DashboardFilterArgs,
  normaliseArrayFilter,
} from '@/hooks/queries/dashboardFilterArgs';

export const mapTrpcTestResultsToWidgetGraphQL = (
  trpcData: TestResultsRegisterResponseRow[]
) => {
  const byControl = new Map<string, TestResultsRegisterResponseRow[]>();
  for (const testResult of trpcData) {
    const parentId = testResult.parent?.Id ?? testResult.ParentControlId;
    if (parentId == null) {
      continue;
    }
    const existing = byControl.get(parentId);
    if (existing) {
      existing.push(testResult);
    } else {
      byControl.set(parentId, [testResult]);
    }
  }

  return {
    control: Array.from(byControl.values()).map((testResults) => ({
      testResults,
    })),
  };
};

export type UseGetWidgetTestResultsArgs =
  DashboardFilterArgs<TestResultDashboardDateField>;

type InternalArgs = UseGetWidgetTestResultsArgs & {
  entityIds?: string[];
};

/**
 * The widget's `where` is applied to the NESTED `testResults` selection, not to
 * `control(where:)`. Controls with no matching result therefore come back with
 * an empty `testResults` array, which the descriptor's `flatMap` drops — the
 * row set is identical to filtering the controls, so `$controlWhere` is left
 * unset and every traversal rides in the test-result predicate.
 *
 * Tag, department and ownership all traverse to the parent control, exactly as
 * `useGetTestResults` does; the three lambdas below are copied from it
 * verbatim rather than re-derived.
 */
export const useWidgetTestResultsGraphqlVariables = (args: InternalArgs) => {
  const { user } = useRisksmartUser();
  const entityWhere = useEntityWhereFilter<Test_Result_Bool_Exp>(
    Parent_Type_Enum.TestResult,
    {
      RatingType: { _in: ['assessment', 'rating'] },
    }
  );

  const { where } = buildDashboardWhere({
    base: entityWhere,
    filters: args,
    userId: user?.userId,
    datePredicates: TEST_RESULT_DATE_PREDICATES,
    tagsWhere: (ids) => ({
      parent: { tags: { TagTypeId: { _in: ids } } },
    }),
    departmentsWhere: (ids) => ({
      parent: { departments: { DepartmentTypeId: { _in: ids } } },
    }),
    ownershipWhere: (conditions) => ({
      parent: conditions.controlFilterConditions,
    }),
  });

  // `$where` is non-null in the document. `base` is always supplied here, so
  // `where` is never undefined in practice; the fallback keeps the type honest.
  return { where: where ?? {} };
};

const useGetWidgetTestResultsBase = createQueryHook<
  InternalArgs,
  TestResultsRegisterResponseRow[],
  GetWidgetTestResultsQuery
>({
  trpcQueryOptions: (
    trpc,
    { entityIds, tagTypeIds, departmentTypeIds, dateRange, ownership }
  ) =>
    trpc.frontend.testResult.testResults.queryOptions({
      entityIds,
      tagTypeIds: normaliseArrayFilter(tagTypeIds),
      departmentTypeIds: normaliseArrayFilter(departmentTypeIds),
      dateRange,
      ownership,
    }),
  mapTrpcDataToGraphQL: mapTrpcTestResultsToWidgetGraphQL,
  graphqlDocument: GetWidgetTestResultsDocument,
  graphqlVariables: useWidgetTestResultsGraphqlVariables,
});

/**
 * The control-test gigawidget's read: the same `testResult.testResults`
 * procedure the register page uses, reshaped to the group-by-control selection
 * `getWidgetTestResults.graphql` returns. Entity filtering is resolved
 * internally from `useEntityFilter`; dashboard filters are all optional.
 */
export const useGetWidgetTestResults = (params: {
  queryArgs: UseGetWidgetTestResultsArgs;
  shouldSkip?: boolean;
  suppressErrorNotification?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetWidgetTestResultsBase({
    queryArgs: {
      ...params.queryArgs,
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
    suppressErrorNotification: params.suppressErrorNotification,
  });
};
